import HarmonicsData from '../harmonics/harmonics';
import type { HarmonicSequenceValue } from '../harmonics/harmonics';
import { linearSpacing } from '../../utils';
import { getOutputFrequencyAmplitude } from '../../utils';

export interface WaveformGraphLimits {
  mintime: number;
  maxtime: number;
  tickstime: number;
  minamp: number;
  maxamp: number;
  ticksamp: number;
}

export default class WaveformGraphModel {
  private readonly targetFrequency: number;
  private readonly systemClock: number;
  private readonly harmonicsData: HarmonicsData;

  constructor(targetFrequency: number, systemClock: number, harmonicsData: HarmonicsData) {
    this.targetFrequency = targetFrequency;
    this.harmonicsData = harmonicsData;
    this.systemClock = systemClock;
  }

  get limits(): WaveformGraphLimits {
    return {
      mintime: this.timeDomain[0] as number,
      maxtime: this.waveformMaxtime,
      tickstime: 6,
      minamp: -1.5,
      maxamp: 1.5,
      ticksamp: 5,
    };
  }

  get data(): number[][][] {
    const dacImages = this.harmonicsData.rawHarmonicsTable[0] as HarmonicSequenceValue[];
    const timeDomain = this.timeDomain;
    const orderFirst = this.orderFirst;
    const result = [this.spreadOutCollection(orderFirst, dacImages.length, timeDomain.length)];
    return result;
  }

  private get timeDomain(): number[] {
    const timeDomain = linearSpacing(0.0, (1.0 / this.targetFrequency) * 5, 600);
    return timeDomain;
  }

  private get waveformMaxtime(): number {
    const lastTimeDomainItem = this.timeDomain[this.timeDomain.length - 1] as number; // because we know this is not sparse
    const result =
      lastTimeDomainItem < (1.0 / this.targetFrequency) * 5 ? lastTimeDomainItem : (1.0 / this.targetFrequency) * 5;

    return result;
  }

  private get orderFirst(): number[][][] {
    const dacImages = this.harmonicsData.rawHarmonicsTable[0] as HarmonicSequenceValue[];
    const systemClock = this.systemClock;
    const result = [];
    for (const dacImage of dacImages) {
      const sequenceFrequency = dacImage.sequenceFrequency;

      const xyArray = [];
      let amplitudeDomain = 0;
      for (const timeDomainItem of this.timeDomain) {
        if (sequenceFrequency != null) {
          const fundamentalAttenuation = getOutputFrequencyAmplitude(systemClock, parseFloat(sequenceFrequency));
          amplitudeDomain =
            fundamentalAttenuation * Math.sin(parseFloat(sequenceFrequency) * (2.0 * Math.PI) * timeDomainItem);
        }

        const filterAttenuation = dacImage.filterValues.filterAttenuation;
        if (filterAttenuation && amplitudeDomain) {
          amplitudeDomain = amplitudeDomain * filterAttenuation;
        }
        xyArray.push([timeDomainItem, amplitudeDomain]);
      }

      result.push(xyArray);
    }

    return result;
  }

  private spreadOutCollection(collection: number[][][], loopCount: number, stepCount: number): number[][] {
    const result = [];

    for (let i = 0; i < stepCount; i++) {
      let amplitudeSum = 0;
      // The following scary cast-monster is saying that we're assuming the collection is not sparse.
      const timeDomain = ((collection[0] as number[][])[i] as number[])[0] as number;
      for (let j = 0; j < loopCount; j++) {
        amplitudeSum += ((collection[j] as number[][])[i] as number[])[1] as number;
      }
      result.push([timeDomain, amplitudeSum]);
    }

    return result;
  }
}
